import React, { useState, useMemo, useEffect } from 'react';
import { X, Calendar, Clock, Sparkles, CheckCircle, ChevronLeft, ChevronRight, AlertCircle, Crown } from 'lucide-react';
import scheduleService from '../services/scheduleService';
import { groupSlotsByDuration, filterBookableSlots, BookingMode } from '../services/scheduleModels';
import GlobalLoader from './GlobalLoader';

// ─────────────────────────────────────────────────────────────────────────────
// Modal de horarios de UNA doctora.
//
// Réplica del flujo "Agendar por doctora" de la app Flutter
// (`ScheduleDoctorUnifiedPageWidget`): el backend (endpoint
// `get_professional_schedule_today_to_three_days_after` con
// `unified_view=true, source="daisies"`) devuelve slots atómicos y la duración
// final se obtiene con `groupSlotsByDuration` (1:1 o 2:1).
//
// Slots se muestran como bloques `HH:MM - HH:MM` con badge "Bajo confirmación"
// cuando el `booking_mode` es `on_demand`. El diseño (colores Daisies, cards
// redondeados) se mantiene.
// ─────────────────────────────────────────────────────────────────────────────

const DAY_NAMES_ES = {
  Monday: 'Lun', Tuesday: 'Mar', Wednesday: 'Mié', Thursday: 'Jue',
  Friday: 'Vie', Saturday: 'Sáb', Sunday: 'Dom',
  Lunes: 'Lun', Martes: 'Mar', Miércoles: 'Mié', Jueves: 'Jue',
  Viernes: 'Vie', Sábado: 'Sáb', Domingo: 'Dom'
};

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const formatDayCompact = (dateString, weekDay) => {
  if (!dateString) return weekDay || '';
  const date = new Date(dateString + 'T00:00:00');
  const dayLabel = DAY_NAMES_ES[weekDay] || weekDay || '';
  return `${dayLabel} ${date.getDate()} ${MONTHS_ES[date.getMonth()]}`;
};

const formatDayFull = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
};

// Una "BookingSlot" es la unidad final que se muestra en el grid.
// El mismo shape se exporta hacia App.js cuando el usuario confirma.
const buildSlotPayload = (bookingSlot, dateString, weekDay, doctor, durationOption) => ({
  // Campos canónicos (Flutter-style)
  hourStart: bookingSlot.hourStart,
  hourStop: bookingSlot.hourStop,
  scheduleId: bookingSlot.slotId,
  bookingMode: bookingSlot.bookingMode,
  isOnDemand: bookingSlot.bookingMode === BookingMode.ON_DEMAND,
  isExtra: bookingSlot.isExtra,
  source: bookingSlot.source,
  components: bookingSlot.components,
  date: dateString,
  weekDay,
  doctor,
  durationId: durationOption?.id,
  durationMinutes: durationOption?.durationMinutes,

  // Campos legacy que el resto del flujo (CheckoutFlow, ConfirmationScreen, etc.)
  // espera con los nombres actuales. Mantenemos backward-compat.
  id: bookingSlot.slotId,
  init: bookingSlot.hourStart,
  end: bookingSlot.hourStop,
  hour: bookingSlot.hourStart,
  // `isLocked: true` = slot cargado por staff de Daisies (garantizado).
  // NO indica indisponibilidad: el slot es 100% bookable.
  available: true,
  duration_id: durationOption?.id,
  professional_ids: bookingSlot.professionalIds && bookingSlot.professionalIds.length > 0
    ? bookingSlot.professionalIds
    : [{ id: doctor?.id }],
  professional_id: doctor?.id,
  week_day: weekDay,
  full_date: dateString,
});

const DoctorScheduleModal = ({
  doctor,
  durations = [], // [{ id, durationMinutes, isExtra }]
  authToken,
  userId,
  onSelect,
  onClose,
  onOndemandRequest,
}) => {
  const [activeDurationId, setActiveDurationId] = useState(durations[0]?.id || null);
  const [selectedBookingSlot, setSelectedBookingSlot] = useState(null);
  const [selectedDateString, setSelectedDateString] = useState(null);
  const [startIndex, setStartIndex] = useState(0);

  // El backend devuelve la semana para una duración a la vez; cuando el usuario
  // cambia de pestaña, refetch.
  const [weekSchedule, setWeekSchedule] = useState({ days: [], slotDurationMinutes: 0, hasAvailability: false, success: false });
  const [loading, setLoading] = useState(false);

  const activeDuration = useMemo(
    () => durations.find(d => d.id === activeDurationId) || durations[0] || null,
    [durations, activeDurationId]
  );

  useEffect(() => {
    if (!activeDuration?.id || !doctor?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setSelectedBookingSlot(null);
      setSelectedDateString(null);
      setStartIndex(0);

      // Misma ventana que el cliente Flutter (today → today + 7d). La app usa
      // "today_to_three_days_after" pero el backend respeta date_start/date_end.
      const today = new Date();
      const end = new Date();
      end.setDate(today.getDate() + 7);
      const res = await scheduleService.getProfessionalWeekSchedule({
        doctorId: doctor.id,
        userId: userId || undefined,
        durationId: activeDuration.id,
        dateStart: today,
        dateEnd: end,
        authToken: authToken || '',
        tz: doctor.tz || undefined,
      });
      if (!cancelled) {
        setWeekSchedule(res);
        setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [activeDuration?.id, doctor?.id, authToken, userId]);

  // Agrupa cada día en BookingSlots según la duración objetivo.
  // Si el backend reporta `slot_duration_minutes`, agrupa 1:1 o 2:1 igual que
  // la app Flutter. Si no, asume 1:1 (lo cual también es lo que hace Flutter
  // como fallback).
  const daysWithBookingSlots = useMemo(() => {
    const targetMin = activeDuration?.durationMinutes || weekSchedule.slotDurationMinutes || 0;
    const atomicMin = weekSchedule.slotDurationMinutes || targetMin;
    return (weekSchedule.days || []).map(day => {
      const bookingSlots = groupSlotsByDuration({
        slots: day.slots,
        targetDurationMinutes: targetMin,
        atomicSlotMinutes: atomicMin,
      });
      // No mostrar slots pasados ni dentro del mínimo de anticipación: solo
      // horarios que la doctora realmente puede confirmar (>= now + 6h).
      const bookableSlots = filterBookableSlots(day.date, bookingSlots);
      return { ...day, bookingSlots: bookableSlots };
    }).filter(d => d.bookingSlots.length > 0);
  }, [weekSchedule, activeDuration]);

  // isLocked NO indica indisponibilidad — todos los slots devueltos son bookables.
  const hasAnyAvailable = useMemo(
    () => daysWithBookingSlots.some(d => d.bookingSlots.length > 0),
    [daysWithBookingSlots]
  );

  // Slot sugerido: el primero del primer día.
  const suggestedSlot = useMemo(() => {
    for (const day of daysWithBookingSlots) {
      const first = day.bookingSlots[0];
      if (first) return { day, bookingSlot: first };
    }
    return null;
  }, [daysWithBookingSlots]);

  // Otros horarios: excluye el sugerido.
  const otherDays = useMemo(() => {
    if (!suggestedSlot) return daysWithBookingSlots;
    return daysWithBookingSlots.map(day => {
      const filteredSlots = day.bookingSlots.filter(s => !(
        day.date === suggestedSlot.day.date &&
        s.hourStart === suggestedSlot.bookingSlot.hourStart &&
        s.slotId === suggestedSlot.bookingSlot.slotId
      ));
      return { ...day, bookingSlots: filteredSlots };
    }).filter(d => d.bookingSlots.length > 0);
  }, [daysWithBookingSlots, suggestedSlot]);

  const handleSlotClick = (bookingSlot, dateString, weekDay) => {
    setSelectedBookingSlot(bookingSlot);
    setSelectedDateString(dateString);
    // weekDay se guarda implícitamente en la slot al confirmar
  };

  const handleConfirm = () => {
    if (!selectedBookingSlot || !selectedDateString) return;
    // Buscar weekDay del día que contiene la slot
    const day = daysWithBookingSlots.find(d => d.date === selectedDateString);
    onSelect(buildSlotPayload(selectedBookingSlot, selectedDateString, day?.weekDay, doctor, activeDuration));
  };

  const handleNext = () => {
    if (startIndex + 3 < otherDays.length) setStartIndex(startIndex + 1);
  };
  const handlePrev = () => {
    if (startIndex > 0) setStartIndex(startIndex - 1);
  };

  const isSelected = (bookingSlot, dateString) => {
    if (!selectedBookingSlot || selectedDateString !== dateString) return false;
    return (
      selectedBookingSlot.hourStart === bookingSlot.hourStart &&
      selectedBookingSlot.slotId === bookingSlot.slotId
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDERIZADO
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-card shadow-elevated max-w-2xl w-full my-8 max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 md:p-6 border-b border-brand-100 flex-shrink-0">
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              <img
                src={doctor?.avatar}
                alt={`${doctor?.name || ''} ${doctor?.last_name || ''}`}
                className="w-12 h-12 rounded-full object-cover"
                onError={(e) => {
                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doctor?.name || 'Dra')}&background=786ED9&color=fff`;
                }}
              />
              <div>
                <h3 className="text-lg md:text-xl font-bold text-text-primary leading-tight">
                  Horarios disponibles
                </h3>
                <p className="text-text-secondary text-sm">
                  {doctor?.name} {doctor?.last_name} • {doctor?.specialty?.name || 'Especialista'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-text-secondary hover:text-text-primary transition-colors"
              aria-label="Cerrar"
            >
              <X size={22} />
            </button>
          </div>

          {/* Pestañas de duración (Flutter: tabs de "Consulta corta / larga") */}
          {durations.length > 0 && (
            <div className="flex border-b border-brand-100 -mx-5 md:-mx-6 px-5 md:px-6">
              {durations.map((dur) => (
                <button
                  key={dur.id}
                  onClick={() => {
                    setActiveDurationId(dur.id);
                    setSelectedBookingSlot(null);
                  }}
                  className={`flex-1 py-2.5 text-sm font-semibold text-center transition-all relative ${
                    activeDurationId === dur.id ? 'text-daisies' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  {dur.durationMinutes ? `${dur.durationMinutes} min` : 'Consulta'}
                  {activeDurationId === dur.id && (
                    <span className="absolute bottom-0 left-0 right-0 h-[3px] bg-daisies rounded-t-full" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-10">
              <GlobalLoader title="Cargando horarios" subtitle="Buscando disponibilidad..." overlay={false} />
            </div>
          ) : !hasAnyAvailable ? (
            <div className="text-center py-12 px-6">
              <Calendar className="mx-auto mb-4 text-text-secondary" size={48} />
              <h4 className="text-base font-semibold text-text-primary mb-1">Sin horarios disponibles</h4>
              <p className="text-text-secondary text-sm">
                Esta doctora no tiene horarios libres en los próximos días.
              </p>
            </div>
          ) : (
            <>
              {/* Horario sugerido */}
              {suggestedSlot && (
                <div className="px-5 md:px-6 pt-5 pb-4 bg-daisies-50 border-b border-brand-100">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="text-amber-pill fill-amber-pill" size={18} />
                    <h4 className="text-base font-bold text-text-primary">Horario sugerido</h4>
                    <span className="ml-auto ds-caption font-semibold px-2 py-0.5 bg-daisies-50 text-daisies rounded-full">
                      Recomendado
                    </span>
                  </div>

                  <SlotButton
                    bookingSlot={suggestedSlot.bookingSlot}
                    dateString={suggestedSlot.day.date}
                    weekDay={suggestedSlot.day.weekDay}
                    isSelected={isSelected(suggestedSlot.bookingSlot, suggestedSlot.day.date)}
                    onClick={handleSlotClick}
                    variant="suggested"
                  />
                </div>
              )}

              {/* Otros horarios */}
              <div className="p-5 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm md:text-base font-semibold text-text-primary">
                    {otherDays.length > 0 ? 'Otros horarios disponibles' : 'Horarios'}
                  </h4>
                  {otherDays.length > 3 && (
                    <div className="flex gap-2">
                      <button
                        onClick={handlePrev}
                        disabled={startIndex === 0}
                        className={`p-1.5 rounded-full bg-daisies-50 transition-all ${
                          startIndex === 0
                            ? 'text-text-secondary/40 cursor-not-allowed'
                            : 'text-daisies hover:bg-daisies-100'
                        }`}
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        onClick={handleNext}
                        disabled={startIndex + 3 >= otherDays.length}
                        className={`p-1.5 rounded-full bg-daisies-50 transition-all ${
                          startIndex + 3 >= otherDays.length
                            ? 'text-text-secondary/40 cursor-not-allowed'
                            : 'text-daisies hover:bg-daisies-100'
                        }`}
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="space-y-5">
                  {otherDays.slice(startIndex, startIndex + 3).map((day) => (
                    <div key={day.date}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <Calendar className="text-daisies" size={16} />
                        <h5 className="font-bold text-sm text-text-primary">
                          {formatDayCompact(day.date, day.weekDay)}
                        </h5>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {day.bookingSlots.map((bs, idx) => (
                          <SlotButton
                            key={`${day.date}-${bs.slotId ?? idx}-${bs.hourStart}`}
                            bookingSlot={bs}
                            dateString={day.date}
                            weekDay={day.weekDay}
                            isSelected={isSelected(bs, day.date)}
                            onClick={handleSlotClick}
                            variant="grid"
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer: resumen + acciones */}
        {selectedBookingSlot ? (
          <div className="flex-shrink-0 border-t border-brand-100 bg-white p-5 md:p-6">
            <div className="mb-3 p-3 bg-daisies-50 rounded-input">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-input text-white ${selectedBookingSlot.bookingMode === BookingMode.ON_DEMAND ? 'bg-amber-500' : 'bg-daisies'}`}>
                  {selectedBookingSlot.bookingMode === BookingMode.ON_DEMAND ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-secondary">Horario seleccionado</p>
                  <p className="font-semibold text-sm text-text-primary truncate">
                    {formatDayFull(selectedDateString)} • {selectedBookingSlot.hourStart} - {selectedBookingSlot.hourStop}
                  </p>
                  {selectedBookingSlot.bookingMode === BookingMode.ON_DEMAND && (
                    <p className="ds-caption text-amber-700 font-medium mt-0.5">Requiere confirmación de la doctora</p>
                  )}
                </div>
                <button
                  onClick={() => { setSelectedBookingSlot(null); setSelectedDateString(null); }}
                  className="press-scale ds-caption text-daisies hover:text-daisies-hover font-semibold px-2 py-1 underline"
                >
                  Cambiar
                </button>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="press-scale flex-1 px-5 py-3 border-[1.5px] border-brand text-daisies rounded-full font-semibold hover:bg-daisies/5 transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                className="press-scale flex-1 px-5 py-3 bg-daisies text-white rounded-full font-semibold hover:bg-daisies-hover transition-all"
              >
                Continuar
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 border-t border-brand-100 bg-white p-5 md:p-6 space-y-3">
            {!hasAnyAvailable && onOndemandRequest && (
              <button
                onClick={() => onOndemandRequest(doctor)}
                className="press-scale w-full py-3 px-6 bg-daisies-50 text-daisies rounded-full font-bold hover:bg-daisies-100 transition-all flex items-center justify-center gap-2 group"
              >
                <Sparkles size={16} className="group-hover:rotate-12 transition-transform" />
                Proponer horario SOS
              </button>
            )}
            <button
              onClick={onClose}
              className="press-scale w-full py-2.5 text-text-secondary text-sm font-semibold hover:underline"
            >
              Cerrar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Botón individual de slot (sugerido o grid).
// `isLocked` indica que el slot fue cargado por staff de Daisies (garantizado),
// NO que esté desactivado — todos los slots devueltos son bookables.
const SlotButton = ({ bookingSlot, dateString, weekDay, isSelected, onClick, variant = 'grid' }) => {
  const isOnDemand = bookingSlot.bookingMode === BookingMode.ON_DEMAND;
  const isStaffGuaranteed = bookingSlot.isLocked;

  if (variant === 'suggested') {
    return (
      <button
        onClick={() => onClick(bookingSlot, dateString, weekDay)}
        className={`w-full p-4 rounded-input transition-all transform ${
          isSelected
            ? 'bg-daisies text-white scale-[1.02] shadow-elevated ring-2 ring-daisies/30'
            : isOnDemand
              ? 'bg-amber-pill/15 hover:bg-amber-pill/25 hover:scale-[1.02]'
              : 'bg-white hover:scale-[1.02]'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-input ${
              isSelected ? 'bg-white text-daisies'
              : isOnDemand ? 'bg-amber-100 text-amber-700'
              : 'bg-daisies text-white'
            }`}>
              <Clock size={20} />
            </div>
            <div className="text-left">
              <div className={`font-bold text-base ${isSelected ? 'text-white' : 'text-text-primary'}`}>
                {bookingSlot.hourStart} - {bookingSlot.hourStop}
              </div>
              <div className={`text-xs ${isSelected ? 'text-white/85' : 'text-text-secondary'}`}>
                {formatDayFull(dateString)}
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {isOnDemand && !isSelected && (
                  <span className="inline-flex items-center gap-1 ds-caption font-semibold text-amber-700">
                    <AlertCircle size={11} /> Bajo confirmación
                  </span>
                )}
                {isStaffGuaranteed && !isOnDemand && !isSelected && (
                  <span className="inline-flex items-center gap-1 ds-caption font-semibold text-mint-deep">
                    ✓ Garantizado
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className={`p-2 rounded-full ${isSelected ? 'bg-white text-daisies' : isOnDemand ? 'bg-amber-50 text-amber-700' : 'bg-daisies-50 text-daisies'}`}>
            <CheckCircle size={20} />
          </div>
        </div>
      </button>
    );
  }

  // Grid variant
  return (
    <button
      onClick={() => onClick(bookingSlot, dateString, weekDay)}
      className={`press-scale p-3 rounded-input transition-all text-left relative ${
        isSelected
          ? 'bg-daisies text-white shadow-elevated'
          : isOnDemand
            ? 'bg-amber-pill/15 hover:bg-amber-pill/25'
            : 'bg-white hover:bg-daisies-50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className={`font-semibold text-sm flex items-center gap-1 ${isSelected ? 'text-white' : isOnDemand ? 'text-amber-800' : 'text-daisies'}`}>
            <Clock size={13} />
            {bookingSlot.hourStart}
          </div>
          <div className={`ds-caption mt-0.5 ${isSelected ? 'text-white/80' : 'text-text-secondary'}`}>
            {bookingSlot.hourStop}
          </div>
        </div>
        {bookingSlot.isExtra && (
          <Crown size={12} className={isSelected ? "text-amber-pill" : "text-amber-pill"} title="Consulta extra" />
        )}
      </div>
      {isOnDemand && !isSelected && (
        <div className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
          SOS
        </div>
      )}
    </button>
  );
};

export default DoctorScheduleModal;
